import { EyeOutlined } from '@ant-design/icons'
import { Button, Table, TableColumnsType } from 'antd'
import { TableProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { GetPersonListResponseItem, PersonCaseStatus } from 'src/api/requests'
import { GenderShortMap } from 'src/features/persons/utils'
import { dayMonthYearFormatWithDot } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

import { CaseStatusTag } from './CaseStatusTag'

type PersonRow = GetPersonListResponseItem & {
  caseStatus?: PersonCaseStatus | null
}

type Props = {
  isPatient?: boolean
  showCaseStatus?: boolean
} & TableProps

export const PersonsTable = ({
  isPatient,
  showCaseStatus = true,
  ...props
}: Props) => {
  const { t } = useLocales()
  const navigate = useNavigate()

  const columns = useMemo(
    (): TableColumnsType<PersonRow> => [
      ...(isPatient
        ? [
            {
              key: 'tarv_code',
              title: t('common.tarv_code'),
              dataIndex: 'tarvCode'
            }
          ]
        : []),
      {
        key: 'last_name',
        width: 270,
        title: t('form.last_name'),
        dataIndex: 'lastName'
      },
      {
        key: 'first_name',
        title: t('form.first_name'),
        dataIndex: 'firstName'
      },
      {
        key: 'idnp',
        title: t('form.idnp'),
        dataIndex: 'idnp'
      },
      ...(isPatient
        ? [
            {
              key: 'sime_code',
              title: t('common.sime_code'),
              dataIndex: 'simeCode'
            }
          ]
        : []),
      {
        key: 'birth_date',
        title: t('form.birth_date'),
        render: (_, { birthDate }) =>
          dayjs(birthDate).format(dayMonthYearFormatWithDot),
        align: 'center'
      },
      {
        key: 'gender',
        title: t('form.gender'),
        render: (_, { gender }) => (gender ? GenderShortMap[gender] : '-'),
        align: 'center',
        width: '84px'
      },
      ...(showCaseStatus
        ? [
            {
              key: 'caseStatus',
              title: t('persons.case_status.title'),
              render: (_, { caseStatus }: PersonRow) => (
                <CaseStatusTag status={caseStatus} />
              ),
              align: 'center' as const
            }
          ]
        : []),
      {
        key: 'actions',
        fixed: 'right',
        title: t('common.action'),
        align: 'center',
        render: ({ id }) => (
          <Button
            icon={<EyeOutlined />}
            onClick={() =>
              navigate(
                isPatient
                  ? paths.patients.details(String(id))
                  : paths.persons.details(String(id))
              )
            }
          >
            {t('common.details')}
          </Button>
        )
      }
    ],
    [t, isPatient, showCaseStatus]
  )

  return <Table rowKey="id" columns={columns} scroll={{ x: true }} {...props} />
}
